(() => {
  const initMobileCarousels = (scope, mobile) => {
    scope.querySelectorAll('[data-sklum-mobile-carousel]').forEach((carousel) => {
      if (carousel.dataset.mobileCarouselReady) return;
      const track = carousel.querySelector('[data-mobile-carousel-track]');
      if (!track) return;
      carousel.dataset.mobileCarouselReady = 'true';
      const controller = new AbortController();
      const listen = (target, event, handler) => target.addEventListener(event, handler, { signal: controller.signal });
      const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
      const configuredDelay = Number(carousel.dataset.autoplayMs) || 0;
      const delay = configuredDelay > 0 ? Math.max(2000, configuredDelay) : 0;
      let timer;
      let observer;
      let visible = false;
      let pointerDown = false;
      let hovered = false;
      const destroy = () => {
        window.clearTimeout(timer);
        observer?.disconnect();
        controller.abort();
        delete carousel.dataset.mobileCarouselReady;
      };
      const advance = (direction) => {
        if (!mobile.matches || !track.firstElementChild) return;
        const gap = parseFloat(getComputedStyle(track).columnGap) || 0;
        const step = track.firstElementChild.getBoundingClientRect().width + gap;
        const maximum = Math.max(0, track.scrollWidth - track.clientWidth);
        if (!step || maximum < 1) return;
        let next = Math.round(track.scrollLeft / step) * step + direction * step;
        if (next > maximum + 1) next = 0;
        if (next < -1) next = maximum;
        track.scrollTo({ left: Math.min(maximum, Math.max(0, next)), behavior: reduced.matches ? 'auto' : 'smooth' });
      };
      const schedule = () => {
        window.clearTimeout(timer);
        if (!carousel.isConnected) { destroy(); return; }
        const active = document.activeElement;
        const keyboardFocus = active && carousel.contains(active) && active.matches(':focus-visible');
        if (!delay || !mobile.matches || !visible || document.hidden || reduced.matches || pointerDown || hovered || keyboardFocus) return;
        timer = window.setTimeout(() => { advance(1); schedule(); }, delay);
      };
      [['[data-mobile-carousel-prev]', -1], ['[data-mobile-carousel-next]', 1]].forEach(([selector, direction]) => {
        const button = carousel.querySelector(selector);
        if (button) listen(button, 'click', () => { advance(direction); schedule(); });
      });
      listen(track, 'keydown', (event) => {
        if (!mobile.matches || event.target.closest('input,select,textarea')) return;
        if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
        event.preventDefault();
        advance(event.key === 'ArrowRight' ? 1 : -1);
        schedule();
      });
      listen(track, 'pointerdown', () => { pointerDown = true; schedule(); });
      const endPointer = () => { if (pointerDown) { pointerDown = false; schedule(); } };
      listen(window, 'pointerup', endPointer);
      listen(window, 'pointercancel', endPointer);
      listen(carousel, 'mouseenter', () => { hovered = window.matchMedia('(hover: hover)').matches; schedule(); });
      listen(carousel, 'mouseleave', () => { hovered = false; schedule(); });
      listen(carousel, 'focusin', () => window.setTimeout(schedule, 0));
      listen(carousel, 'focusout', () => window.setTimeout(schedule, 0));
      listen(document, 'visibilitychange', schedule);
      listen(reduced, 'change', schedule);
      listen(mobile, 'change', () => {
        if (!mobile.matches) track.scrollTo({ left: 0, behavior: 'auto' });
        schedule();
      });
      listen(document, 'shopify:section:unload', (event) => { if (event.target.contains(carousel)) destroy(); });
      if ('IntersectionObserver' in window) {
        observer = new IntersectionObserver(([entry]) => { visible = entry.isIntersecting && entry.intersectionRatio >= .1; schedule(); }, { threshold: [.1] });
        observer.observe(carousel);
      } else { visible = true; schedule(); }
    });
  };
  const boot = (scope = document) => {
    const mobile = window.matchMedia('(max-width: 767px)');
    initMobileCarousels(scope, mobile);
    scope.querySelectorAll('[data-sklum-hero]').forEach((hero) => {
      if (hero.dataset.ready) return;
      hero.dataset.ready = 'true';
      const videos = [...hero.querySelectorAll('.sklum-hero-video')];
      const load = (video) => {
        const source = mobile.matches ? video.dataset.mobileSrc : video.dataset.desktopSrc;
        const poster = mobile.matches ? video.dataset.mobilePoster : video.dataset.desktopPoster;
        if (poster) video.poster = poster;
        if (source && video.getAttribute('src') !== source) {
          video.classList.remove('is-loaded');
          video.src = source;
          video.load();
        }
      };
      videos.forEach((video) => {
        video.muted = true;
        video.addEventListener('loadeddata', () => video.classList.add('is-loaded'));
        video.closest('.sklum-hero-story').querySelector('[data-video-sound]')?.addEventListener('click', (event) => {
          video.muted = !video.muted;
          const button = event.currentTarget;
          button.setAttribute('aria-pressed', String(!video.muted));
          button.setAttribute('aria-label', video.muted ? 'Enable sound' : 'Mute video');
        });
      });
      if ('IntersectionObserver' in window) {
        const observer = new IntersectionObserver((entries) => {
          entries.forEach(({ target, isIntersecting }) => {
            if (isIntersecting) {
              load(target);
              target.play().catch(() => {});
            } else target.pause();
          });
        }, { rootMargin: '200px', threshold: 0.05 });
        videos.forEach((video) => observer.observe(video));
      } else videos.forEach((video) => { load(video); video.play().catch(() => {}); });
      mobile.addEventListener('change', () => videos.forEach((video) => {
        if (video.getAttribute('src')) { load(video); video.play().catch(() => {}); }
      }));
      document.addEventListener('shopify:block:select', (event) => {
        if (hero.contains(event.target)) event.target.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    });
    scope.querySelectorAll('[data-sklum-carousel]').forEach((carousel) => {
      if (carousel.dataset.ready) return;
      carousel.dataset.ready = 'true';
      const track = carousel.querySelector('[data-carousel-track]');
      if (!track) return;
      let paused = false;
      const advance = (direction = 1) => {
        const gap = parseFloat(getComputedStyle(track).columnGap) || 0;
        const width = track.querySelector('.sklum-category-item')?.getBoundingClientRect().width || track.clientWidth;
        const remaining = track.scrollWidth - track.clientWidth;
        let left = track.scrollLeft + direction * (width + gap);
        if (left > remaining + 2) left = 0;
        if (left < -2) left = remaining;
        track.scrollTo({ left: Math.min(remaining, Math.max(0, left)), behavior: 'smooth' });
      };
      carousel.querySelector('[data-carousel-prev]')?.addEventListener('click', () => advance(-1));
      carousel.querySelector('[data-carousel-next]')?.addEventListener('click', () => advance(1));
      carousel.addEventListener('mouseenter', () => { paused = true; });
      carousel.addEventListener('mouseleave', () => { paused = false; });
      carousel.addEventListener('focusin', () => { paused = true; });
      carousel.addEventListener('focusout', () => { paused = false; });
      track.addEventListener('keydown', (event) => {
        if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
          event.preventDefault();
          advance(event.key === 'ArrowRight' ? 1 : -1);
        }
      });
      if (carousel.dataset.autoplay === 'true' && !window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        const timer = window.setInterval(() => {
          if (!carousel.isConnected) { window.clearInterval(timer); return; }
          const rect = carousel.getBoundingClientRect();
          if (!paused && !document.hidden && rect.bottom > 0 && rect.top < innerHeight) advance();
        }, Math.max(3000, Number(carousel.dataset.speed) || 8000));
      }
    });
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => boot());
  else boot();
  document.addEventListener('shopify:section:load', (event) => boot(event.target));
})();
